import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../styles/DeleteAccount.css";
import Header from "./Header";
import Footer from "./Footer";
import Sidebar from "./Sidebar";
import { hostAndBackendPort } from "../utils/constants";
import { useLanguage } from "../context/Language";

// Website-only account deletion API:
//
// POST /api/website/account-deletion/request
//   { identifier }
//   -> { ok, channel }
//
// POST /api/website/account-deletion/verify
//   { identifier, code }
//   -> { ok, deletionToken }
//
// POST /api/website/account-deletion/confirm
//   { deletionToken, confirm: "DELETE" }
//   -> { ok }
//
// This website flow is independent from the authenticated mobile-app
// deletion endpoint. The backend must not reveal whether an account exists.

const STEP = {
  IDENTIFY: "identify",
  VERIFY: "verify",
  CONFIRM: "confirm",
  DONE: "done",
};

const TAB = {
  EMAIL: "email",
  PHONE: "phone",
};

const RESEND_COOLDOWN_SECONDS = 30;

const WEBSITE_DELETION_ENDPOINT = `${hostAndBackendPort}/website/account-deletion`;

const DELETE_CONFIRMATION_WORDS = {
  en: "DELETE",
  fr: "SUPPRIMER",
  es: "ELIMINAR",
  ar: "حذف",
  it: "ELIMINA",
};

const DeleteAccountPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const deleteConfirmationWord =
    DELETE_CONFIRMATION_WORDS[language.value] || DELETE_CONFIRMATION_WORDS.en;
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");
  const DELETION_REASONS = [
    ["met_on_mabrook", t("I met someone on Mabrook")],
    ["met_elsewhere", t("I met someone elsewhere")],
    ["not_enough_profiles", t("Not enough profiles near me")],
    ["too_expensive", t("Too expensive")],
    ["taking_a_break", t("I'm taking a break")],
    ["privacy", t("Privacy concerns")],
    ["bad_experience", t("Bad experience")],
    ["other", t("Other")],
  ];

  const normalizedConfirmText = confirmText.trim().toLocaleUpperCase();

  const normalizedConfirmationWord = deleteConfirmationWord.toLocaleUpperCase();

  const isDeleteConfirmationValid =
    normalizedConfirmText === normalizedConfirmationWord;

  const [step, setStep] = useState(STEP.IDENTIFY);

  // Identify-step state. Email and phone are tracked independently so
  // switching tabs doesn't wipe out what the user already typed on the
  // other tab.
  const [activeTab, setActiveTab] = useState(TAB.EMAIL);
  const [emailValue, setEmailValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");

  // The identifier actually submitted to the backend. Captured once at
  // request time so the verify/resend flow doesn't depend on tab state
  // (tabs only exist in the identify step).
  const [submittedIdentifier, setSubmittedIdentifier] = useState("");

  const [channel, setChannel] = useState(null); // "email" | "phone", from backend response
  const [code, setCode] = useState("");
  const [deletionToken, setDeletionToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const cooldownRef = useRef(null);
  const tabRefs = useRef({});

  useEffect(() => {
    return () => cooldownRef.current && clearInterval(cooldownRef.current);
  }, []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const normalizePhone = (value) => value.replace(/[\s().-]/g, "");

  const isValidPhone = (value) =>
    /^\+[1-9]\d{7,14}$/.test(normalizePhone(value));

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setError("");
  };

  const handleTabKeyDown = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next = activeTab === TAB.EMAIL ? TAB.PHONE : TAB.EMAIL;
    handleTabChange(next);
    tabRefs.current[next]?.focus();
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");

    const rawValue = activeTab === TAB.EMAIL ? emailValue : phoneValue;

    const identifier =
      activeTab === TAB.EMAIL
        ? rawValue.trim().toLowerCase()
        : normalizePhone(rawValue);

    const valid =
      activeTab === TAB.EMAIL
        ? isValidEmail(identifier)
        : isValidPhone(identifier);

    if (!valid) {
      setError(
        activeTab === TAB.EMAIL
          ? t("Enter a valid email address.")
          : t(
              "Enter a phone number with its country code, for example +212612345678.",
            ),
      );
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${WEBSITE_DELETION_ENDPOINT}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier,
          languageCode: language.value,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || t("Something went wrong. Please try again."));
        return;
      }

      setSubmittedIdentifier(identifier);
      setChannel(data.channel || activeTab);
      setCode("");
      setStep(STEP.VERIFY);
      startCooldown();
    } catch {
      setError(
        t("We couldn't reach the server. Check your connection and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;

    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${WEBSITE_DELETION_ENDPOINT}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: submittedIdentifier,
          languageCode: language.value,
        }),
      });

      if (!res.ok) {
        setError(t("Something went wrong. Please try again."));
        return;
      }

      setCode("");
      startCooldown();
    } catch {
      setError(
        t("We couldn't reach the server. Check your connection and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedCode = code.trim();

    if (!/^\d{6}$/.test(trimmedCode)) {
      setError(t("That code isn't valid. Check and try again."));
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${WEBSITE_DELETION_ENDPOINT}/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: submittedIdentifier,
          code: trimmedCode,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.deletionToken) {
        setError(t("That code isn't valid. Check and try again."));
        return;
      }

      setDeletionToken(data.deletionToken);
      setConfirmText("");
      setStep(STEP.CONFIRM);
    } catch {
      setError(
        t("We couldn't reach the server. Check your connection and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    setError("");

    if (!isDeleteConfirmationValid) {
      setError(
        t("Type {{word}} exactly to confirm.", {
          word: deleteConfirmationWord,
        }),
      );
      return;
    }

    if (!deletionToken) {
      setError(t("Your verification has expired. Please start again."));
      setStep(STEP.IDENTIFY);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${WEBSITE_DELETION_ENDPOINT}/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          deletionToken,
          confirm: "DELETE",
          reason: reason || undefined,
          reasonDetails: reasonDetails.trim() || undefined,
        }),
      });

      if (!res.ok) {
        setError(t("Something went wrong. Please try again."));
        return;
      }

      setDeletionToken(null);
      setStep(STEP.DONE);
    } catch {
      setError(
        t("We couldn't reach the server. Check your connection and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const stepNumber = { identify: 1, verify: 2, confirm: 3, done: 3 }[step];

  return (
    <>
      <Header />
      <div className="da-page">
        <div className="da-card">
          <div className="da-header">
            <span className="da-eyebrow">{t("Account deletion")}</span>
            <h1 className="da-title">{t("Delete your Mabrook account")}</h1>
          </div>

          {step !== STEP.DONE && (
            <div className="da-steps" aria-hidden="true">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div
                    className={`da-step-dot ${n <= stepNumber ? "is-active" : ""}`}
                  >
                    {n < stepNumber ? "✓" : n}
                  </div>
                  {n < 3 && (
                    <div
                      className={`da-step-line ${n < stepNumber ? "is-active" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {step === STEP.IDENTIFY && (
            <>
              <p className="da-body">
                {t(
                  "Choose how you signed up. We'll send a verification code to confirm it's you.",
                )}
              </p>

              <div className="da-warning">
                <strong>{t("This deletes your account permanently.")}</strong>
                <p>
                  {t(
                    "Your profile, photos, matches, and messages will be removed. This cannot be undone.",
                  )}
                </p>
              </div>

              <div
                className="da-tabs"
                role="tablist"
                aria-label={t("Choose verification method")}
                onKeyDown={handleTabKeyDown}
              >
                <button
                  type="button"
                  role="tab"
                  id="da-tab-email"
                  ref={(el) => (tabRefs.current[TAB.EMAIL] = el)}
                  aria-selected={activeTab === TAB.EMAIL}
                  aria-controls="da-tabpanel-email"
                  tabIndex={activeTab === TAB.EMAIL ? 0 : -1}
                  className={`da-tab ${activeTab === TAB.EMAIL ? "is-active" : ""}`}
                  onClick={() => handleTabChange(TAB.EMAIL)}
                >
                  {t("Email")}
                </button>
                <button
                  type="button"
                  role="tab"
                  id="da-tab-phone"
                  ref={(el) => (tabRefs.current[TAB.PHONE] = el)}
                  aria-selected={activeTab === TAB.PHONE}
                  aria-controls="da-tabpanel-phone"
                  tabIndex={activeTab === TAB.PHONE ? 0 : -1}
                  className={`da-tab ${activeTab === TAB.PHONE ? "is-active" : ""}`}
                  onClick={() => handleTabChange(TAB.PHONE)}
                >
                  {t("Phone")}
                </button>
              </div>

              <form onSubmit={handleRequestCode} noValidate>
                {activeTab === TAB.EMAIL ? (
                  <div
                    role="tabpanel"
                    id="da-tabpanel-email"
                    aria-labelledby="da-tab-email"
                  >
                    <label className="da-label" htmlFor="da-email">
                      {t("Email address")}
                    </label>
                    <input
                      id="da-email"
                      className="da-input"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                ) : (
                  <div
                    role="tabpanel"
                    id="da-tabpanel-phone"
                    aria-labelledby="da-tab-phone"
                  >
                    <label className="da-label" htmlFor="da-phone">
                      {t("Phone number")}
                    </label>
                    <input
                      id="da-phone"
                      className="da-input"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+212 612 345 678"
                      value={phoneValue}
                      onChange={(e) => {
                        setPhoneValue(e.target.value);
                        setError("");
                      }}
                      disabled={loading}
                    />
                  </div>
                )}

                {error && (
                  <p className="da-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="da-button da-button-primary"
                  disabled={loading}
                >
                  {loading ? t("Please wait...") : t("Send verification code")}
                </button>
              </form>
            </>
          )}

          {step === STEP.VERIFY && (
            <>
              <p className="da-body">
                {channel === "phone"
                  ? t(
                      "If a Mabrook account is associated with this phone number, we sent a 6-digit verification code by SMS to:",
                    )
                  : t(
                      "If a Mabrook account is associated with this email address, we sent a 6-digit verification code by email to:",
                    )}
              </p>
              <p className="da-identifier">
                <strong dir="ltr">{submittedIdentifier}</strong>
              </p>

              <form onSubmit={handleVerifyCode} noValidate>
                <label className="da-label" htmlFor="da-code">
                  {t("Verification code")}
                </label>

                <input
                  id="da-code"
                  className="da-input da-input-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, "");
                    setCode(digitsOnly);
                  }}
                  disabled={loading}
                />

                <p className="da-help-text">
                  {t(
                    "Didn't receive a code? Check that you entered the email address or phone number associated with your Mabrook account.",
                  )}
                </p>

                {error && (
                  <p className="da-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="da-button da-button-primary"
                  disabled={loading}
                >
                  {loading ? t("Please wait...") : t("Verify code")}
                </button>

                <button
                  type="button"
                  className="da-button da-button-text"
                  onClick={handleResend}
                  disabled={cooldown > 0 || loading}
                >
                  {cooldown > 0
                    ? t("Resend code in {{seconds}}s", {
                        seconds: cooldown,
                      })
                    : t("Resend code")}
                </button>

                <button
                  type="button"
                  className="da-button da-button-text"
                  onClick={() => {
                    setStep(STEP.IDENTIFY);
                    setCode("");
                    setError("");
                    setSubmittedIdentifier("");
                    setDeletionToken(null);
                    setCooldown(0);
                  }}
                  disabled={loading}
                >
                  {t("Use a different email or phone number")}
                </button>
              </form>
            </>
          )}

          {step === STEP.CONFIRM && (
            <>
              <div className="da-warning da-warning-final">
                <strong>{t("Before you continue")}</strong>
                <ul>
                  <li>
                    {t("Your profile and photos will be deleted immediately.")}
                  </li>
                  <li>
                    {t(
                      'Conversations with other members will show "Deleted Account" and become read-only for them.',
                    )}
                  </li>
                  <li>
                    {t(
                      "Some information may be kept as required by law. See our Privacy Policy for details.",
                    )}
                  </li>
                </ul>
              </div>

              <form onSubmit={handleConfirmDelete} noValidate>
                <fieldset className="da-reasons">
                  <legend className="da-label">
                    {t("Why are you deleting your account? (optional)")}
                  </legend>
                  {DELETION_REASONS.map(([value, label]) => (
                    <label key={value} className="da-reason">
                      <input
                        type="radio"
                        name="reason"
                        value={value}
                        checked={reason === value}
                        onChange={() => setReason(value)}
                        disabled={loading}
                      />{" "}
                      {label}
                    </label>
                  ))}
                  {reason === "taking_a_break" && (
                    <p className="da-break-hint">
                      {t(
                        "You can deactivate your account instead: your profile is hidden and you keep your matches and conversations. Please note that an active membership keeps running during deactivation. Open the Mabrook app, go to Settings and choose Deactivate account. You can come back whenever you want.",
                      )}
                    </p>
                  )}
                  <textarea
                    className="da-input"
                    rows={3}
                    maxLength={300}
                    placeholder={t("Tell us more (optional)")}
                    value={reasonDetails}
                    onChange={(e) => setReasonDetails(e.target.value)}
                    disabled={loading}
                  />
                </fieldset>

                <label className="da-label" htmlFor="da-confirm">
                  {t("Type {{word}} to confirm", {
                    word: deleteConfirmationWord,
                  })}
                </label>
                <input
                  id="da-confirm"
                  className="da-input da-input-confirm"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  placeholder={deleteConfirmationWord}
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                    setError("");
                  }}
                  disabled={loading}
                />
                {error && (
                  <p className="da-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="da-button da-button-danger"
                  disabled={loading || !isDeleteConfirmationValid}
                >
                  {loading
                    ? t("Please wait...")
                    : t("Permanently delete my account")}
                </button>
              </form>
            </>
          )}

          {step === STEP.DONE && (
            <div className="da-done">
              <div className="da-done-icon" aria-hidden="true">
                ✓
              </div>

              <h2 className="da-done-title">
                {reason === "met_on_mabrook"
                  ? t("Mabrook!")
                  : t("Your account has been deleted")}
              </h2>

              {reason === "met_on_mabrook" && (
                <>
                  <p className="da-body">
                    {t(
                      "We're so happy for you. We wish you both every happiness.",
                    )}
                  </p>
                  <p className="da-body">
                    {t("Would you like to share your story? Write to us at")}{" "}
                    <a
                      href={`mailto:contact@mabrook.ma?subject=${encodeURIComponent(
                        t("Our Mabrook story"),
                      )}`}
                    >
                      contact@mabrook.ma
                    </a>
                  </p>
                </>
              )}

              <p className="da-body">
                {t(
                  "We've permanently deleted your Mabrook account and associated data.",
                )}
              </p>
            </div>
          )}

          <p className="da-footer">
            {t("Need help instead of deleting your account?")}{" "}
            <a href="mailto:privacy@mabrook.ma">privacy@mabrook.ma</a>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DeleteAccountPage;
